import type { Template } from '../core/types';

/**
 * The puzzle library: clean, realistic snippets plus a catalogue of defects
 * that can be planted into them. Every mutation rewrites exactly one line
 * (`find` must match a code line, including indentation, exactly once), so a
 * seeded generator can compose 1-3 defects per round without ever producing
 * code that does not parse. Two mutations may target the same line; the
 * generator never picks both.
 *
 * This deterministic engine replaces the idea's suggested Claude API
 * generation: rounds are free, offline, and reproducible from a seed.
 */
export const TEMPLATES: Template[] = [
  {
    id: 'js-cart-checkout',
    language: 'javascript',
    title: 'Shopping cart checkout total',
    code: [
      'function cartTotal(items, taxRate) {',
      '  let total = 0;',
      '  for (let i = 0; i < items.length; i += 1) {',
      '    const item = items[i];',
      '    const price = item.price ?? 0;',
      '    total += price * item.quantity;',
      '  }',
      '  if (total >= 100) {',
      '    total *= 0.9; // 10% bulk discount',
      '  }',
      '  const tax = total * taxRate;',
      '  return Math.round((total + tax) * 100) / 100;',
      '}',
    ],
    mutations: [
      {
        id: 'cart-off-by-one',
        category: 'logic',
        tier: 'novice',
        title: 'Loop reads one past the end',
        explanation:
          'The loop condition uses <= so the final iteration reads items[items.length], which is undefined and crashes on .price.',
        fix: 'Use i < items.length so the last index visited is items.length - 1.',
        find: '  for (let i = 0; i < items.length; i += 1) {',
        replace: '  for (let i = 0; i <= items.length; i += 1) {',
      },
      {
        id: 'cart-missing-price-guard',
        category: 'null-safety',
        tier: 'novice',
        title: 'Missing default for absent price',
        explanation:
          'Items without a price make price undefined, and undefined * quantity is NaN, which silently poisons the whole total.',
        fix: 'Fall back to 0 (item.price ?? 0) or reject items without a price.',
        find: '    const price = item.price ?? 0;',
        replace: '    const price = item.price;',
      },
      {
        id: 'cart-uninitialized-total',
        category: 'logic',
        tier: 'intermediate',
        title: 'Accumulator never initialized',
        explanation:
          'total starts as undefined, so the first += produces NaN and every cart totals NaN no matter its contents.',
        fix: 'Initialize the accumulator: let total = 0.',
        find: '  let total = 0;',
        replace: '  let total;',
      },
      {
        id: 'cart-discount-boundary',
        category: 'logic',
        tier: 'intermediate',
        title: 'Discount boundary excludes the advertised threshold',
        explanation:
          'The bulk discount is advertised at $100 or more, but > 100 quietly denies it to a cart of exactly $100.',
        fix: 'Use >= 100 so the boundary case gets the discount.',
        find: '  if (total >= 100) {',
        replace: '  if (total > 100) {',
      },
      {
        id: 'cart-var-decl',
        category: 'style',
        tier: 'novice',
        title: 'var in modern block-scoped code',
        explanation:
          'var is function-scoped and hoisted; mixing it into otherwise let/const code invites shadowing and hoisting surprises.',
        fix: 'Declare with const (the binding is never reassigned).',
        find: '  const tax = total * taxRate;',
        replace: '  var tax = total * taxRate;',
      },
    ],
  },
  {
    id: 'js-user-search',
    language: 'javascript',
    title: 'User search endpoint helper',
    code: [
      'async function findUsers(db, name, limit) {',
      '  const safeLimit = Math.min(Number(limit) || 20, 100);',
      "  const pattern = '%' + name + '%';",
      '  const rows = await db.query(',
      "    'SELECT * FROM users WHERE name LIKE $1 LIMIT $2',",
      '    [pattern, safeLimit],',
      '  );',
      '  return rows.map((r) => ({ id: r.id, name: r.name, email: r.email }));',
      '}',
    ],
    mutations: [
      {
        id: 'search-sql-injection',
        category: 'security',
        tier: 'expert',
        title: 'SQL injection via template interpolation',
        explanation:
          "User input is interpolated straight into the SQL string; a name like `%' OR '1'='1` rewrites the query. The half-migrated placeholder list is the tell.",
        fix: 'Keep the parameterized query: pass $1/$2 placeholders and the values array.',
        find: "    'SELECT * FROM users WHERE name LIKE $1 LIMIT $2',",
        replace: "    `SELECT * FROM users WHERE name LIKE '${pattern}' LIMIT ${safeLimit}`,",
      },
      {
        id: 'search-row-leak',
        category: 'security',
        tier: 'intermediate',
        title: 'Raw rows leak sensitive columns',
        explanation:
          'Returning rows unprojected sends every users column to the caller, including password_hash and reset tokens.',
        fix: 'Map rows to an explicit whitelist of public fields before returning.',
        find: '  return rows.map((r) => ({ id: r.id, name: r.name, email: r.email }));',
        replace: '  return rows;',
      },
      {
        id: 'search-unvalidated-limit',
        category: 'security',
        tier: 'intermediate',
        title: 'Client-controlled limit used unvalidated',
        explanation:
          'limit flows into the query untouched, so a caller can request the entire table (or pass junk that breaks the query).',
        fix: 'Coerce to a number and clamp it: Math.min(Number(limit) || 20, 100).',
        find: '  const safeLimit = Math.min(Number(limit) || 20, 100);',
        replace: '  const safeLimit = limit;',
      },
      {
        id: 'search-exact-match',
        category: 'logic',
        tier: 'novice',
        title: 'LIKE pattern lost its wildcards',
        explanation:
          "Without the surrounding %'s, LIKE degenerates to an exact match and the search only finds users typed in full.",
        fix: "Wrap the term: '%' + name + '%'.",
        find: "  const pattern = '%' + name + '%';",
        replace: '  const pattern = name;',
      },
    ],
  },
  {
    id: 'py-score-report',
    language: 'python',
    title: 'Score file loader and average',
    code: [
      'def load_scores(path):',
      '    with open(path) as f:',
      '        lines = f.read().splitlines()',
      '    scores = []',
      '    for line in lines:',
      '        if not line.strip():',
      '            continue',
      '        scores.append(float(line))',
      '    return scores',
      '',
      '',
      'def average(scores):',
      '    if not scores:',
      '        return 0.0',
      '    return sum(scores) / len(scores)',
    ],
    mutations: [
      {
        id: 'scores-inverted-guard',
        category: 'logic',
        tier: 'novice',
        title: 'Blank-line guard inverted',
        explanation:
          'The condition skips every data line and tries to parse the blank ones, so float("") raises on the first empty line.',
        fix: 'Skip blank lines: if not line.strip(): continue.',
        find: '        if not line.strip():',
        replace: '        if line.strip():',
      },
      {
        id: 'scores-none-guard',
        category: 'null-safety',
        tier: 'intermediate',
        title: 'Empty list slips past the guard',
        explanation:
          'Checking only for None lets an empty list through to sum(scores) / 0, raising ZeroDivisionError.',
        fix: 'Use the truthiness check (if not scores) to cover both None and empty.',
        find: '    if not scores:',
        replace: '    if scores is None:',
      },
      {
        id: 'scores-denominator',
        category: 'logic',
        tier: 'novice',
        title: 'Off-by-one denominator',
        explanation:
          'Dividing by len - 1 skews every average and raises ZeroDivisionError for a single score.',
        fix: 'Divide by len(scores).',
        find: '    return sum(scores) / len(scores)',
        replace: '    return sum(scores) / (len(scores) - 1)',
      },
      {
        id: 'scores-eval',
        category: 'security',
        tier: 'intermediate',
        title: 'eval on file contents',
        explanation:
          'eval executes whatever the file contains; anyone who can write the score file can run arbitrary code in this process.',
        fix: 'Parse numbers with float(line).',
        find: '        scores.append(float(line))',
        replace: '        scores.append(eval(line))',
      },
    ],
  },
  {
    id: 'js-debounce',
    language: 'javascript',
    title: 'Debounce utility',
    code: [
      'function debounce(fn, delayMs) {',
      '  let timer = null;',
      '  return function (...args) {',
      '    if (timer !== null) {',
      '      clearTimeout(timer);',
      '    }',
      '    timer = setTimeout(() => {',
      '      timer = null;',
      '      fn.apply(this, args);',
      '    }, delayMs);',
      '  };',
      '}',
    ],
    mutations: [
      {
        id: 'debounce-throttle',
        category: 'logic',
        tier: 'expert',
        title: 'Debounce silently became first-call-wins',
        explanation:
          'Returning instead of clearing the pending timer drops every later call in the window, so the stale first arguments win: the opposite of debounce semantics.',
        fix: 'clearTimeout(timer) so the newest call resets the window.',
        find: '      clearTimeout(timer);',
        replace: '      return;',
      },
      {
        id: 'debounce-drops-args',
        category: 'logic',
        tier: 'intermediate',
        title: 'Arguments and this dropped on invoke',
        explanation:
          'Calling fn() bare loses both the captured arguments and the this binding the caller relied on.',
        fix: 'Invoke with fn.apply(this, args).',
        find: '      fn.apply(this, args);',
        replace: '      fn();',
      },
      {
        id: 'debounce-zero-delay',
        category: 'logic',
        tier: 'novice',
        title: 'Delay hardcoded to zero',
        explanation:
          'With a 0ms timeout the wrapper fires on the next tick every time; the delayMs parameter is dead and nothing is debounced.',
        fix: 'Pass delayMs to setTimeout.',
        find: '    }, delayMs);',
        replace: '    }, 0);',
      },
      {
        id: 'debounce-var',
        category: 'style',
        tier: 'novice',
        title: 'var for closure state',
        explanation:
          'var hoists to function scope; for closure state shared across calls, let/const communicates intent and avoids hoisting foot-guns.',
        fix: 'Use let timer = null.',
        find: '  let timer = null;',
        replace: '  var timer = null;',
      },
    ],
  },
  {
    id: 'py-auth',
    language: 'python',
    title: 'Password hashing and verification',
    code: [
      'import hmac',
      'import hashlib',
      'import secrets',
      'import logging',
      '',
      'log = logging.getLogger(__name__)',
      '',
      '',
      'def hash_password(password, salt):',
      "    return hashlib.pbkdf2_hmac('sha256', password.encode(), salt, 200_000)",
      '',
      '',
      'def verify(stored_hash, password, salt):',
      '    candidate = hash_password(password, salt)',
      '    if not hmac.compare_digest(stored_hash, candidate):',
      "        log.info('login failed')",
      '        return False',
      '    return True',
      '',
      '',
      'def make_reset_token():',
      '    return secrets.token_urlsafe(32)',
    ],
    mutations: [
      {
        id: 'auth-timing',
        category: 'security',
        tier: 'expert',
        title: 'Timing-unsafe hash comparison',
        explanation:
          '!= short-circuits on the first differing byte, so response timing leaks how much of the hash matched: the classic timing side channel compare_digest exists to prevent.',
        fix: 'Compare with hmac.compare_digest.',
        find: '    if not hmac.compare_digest(stored_hash, candidate):',
        replace: '    if stored_hash != candidate:',
      },
      {
        id: 'auth-md5',
        category: 'security',
        tier: 'novice',
        title: 'Fast unsalted MD5 for passwords',
        explanation:
          'MD5 ignores the salt and is fast enough to brute-force billions of guesses per second; leaked hashes fall to rainbow tables immediately.',
        fix: 'Use a slow salted KDF such as pbkdf2_hmac (or argon2/bcrypt).',
        find: "    return hashlib.pbkdf2_hmac('sha256', password.encode(), salt, 200_000)",
        replace: '    return hashlib.md5(password.encode()).digest()',
      },
      {
        id: 'auth-low-iterations',
        category: 'security',
        tier: 'expert',
        title: 'KDF iteration count far too low',
        explanation:
          'The right algorithm with 100 iterations is nearly as cheap to brute-force as a bare hash; the work factor is the entire point of PBKDF2.',
        fix: 'Use a modern iteration count (OWASP suggests 600k+ for SHA-256).',
        find: "    return hashlib.pbkdf2_hmac('sha256', password.encode(), salt, 200_000)",
        replace: "    return hashlib.pbkdf2_hmac('sha256', password.encode(), salt, 100)",
      },
      {
        id: 'auth-log-password',
        category: 'security',
        tier: 'intermediate',
        title: 'Plaintext password written to logs',
        explanation:
          'Failed-login logging now stores every typo of every real password in plaintext, and log stores are rarely guarded like password databases.',
        fix: 'Log the event without the secret (username or request id at most).',
        find: "        log.info('login failed')",
        replace: "        log.info('login failed for password %s', password)",
      },
      {
        id: 'auth-short-token',
        category: 'security',
        tier: 'intermediate',
        title: 'Reset token too short to be unguessable',
        explanation:
          'token_urlsafe(4) is ~32 bits: a determined attacker can enumerate the space and take over accounts via password reset.',
        fix: 'Use at least 16 bytes of entropy (token_urlsafe(32)).',
        find: '    return secrets.token_urlsafe(32)',
        replace: '    return secrets.token_urlsafe(4)',
      },
    ],
  },
  {
    id: 'js-fetch-retry',
    language: 'javascript',
    title: 'JSON fetch with retries',
    code: [
      'async function fetchJson(url, retries) {',
      '  let lastError = null;',
      '  for (let attempt = 0; attempt <= retries; attempt += 1) {',
      '    try {',
      '      const response = await fetch(url);',
      '      if (!response.ok) {',
      '        throw new Error(`HTTP ${response.status}`);',
      '      }',
      '      return await response.json();',
      '    } catch (error) {',
      '      lastError = error;',
      '      const waitMs = 250 * 2 ** attempt;',
      '      await new Promise((resolve) => setTimeout(resolve, waitMs));',
      '    }',
      '  }',
      '  throw lastError;',
      '}',
    ],
    mutations: [
      {
        id: 'fetch-status-check',
        category: 'null-safety',
        tier: 'intermediate',
        title: 'Only 500 counts as failure',
        explanation:
          'A 404 or 403 sails past this check and the error page gets parsed as JSON, surfacing as a confusing SyntaxError far from the cause.',
        fix: 'Check response.ok, which covers every non-2xx status.',
        find: '      if (!response.ok) {',
        replace: '      if (response.status === 500) {',
      },
      {
        id: 'fetch-retry-boundary',
        category: 'logic',
        tier: 'expert',
        title: 'Zero attempts when retries is 0',
        explanation:
          'With < retries, retries = 0 means the loop body never runs and the function throws null; retries means attempts *after* the first, so the first try must always happen.',
        fix: 'Loop while attempt <= retries.',
        find: '  for (let attempt = 0; attempt <= retries; attempt += 1) {',
        replace: '  for (let attempt = 0; attempt < retries; attempt += 1) {',
      },
      {
        id: 'fetch-no-backoff',
        category: 'performance',
        tier: 'intermediate',
        title: 'First retry hammers with zero wait',
        explanation:
          'attempt is 0 on the first failure, so 250 * attempt waits 0ms and immediately re-hits a service that is likely already struggling.',
        fix: 'Use exponential backoff, e.g. 250 * 2 ** attempt.',
        find: '      const waitMs = 250 * 2 ** attempt;',
        replace: '      const waitMs = 250 * attempt;',
      },
      {
        id: 'fetch-swallow-error',
        category: 'logic',
        tier: 'intermediate',
        title: 'HTTP error demoted to a console warning',
        explanation:
          'Without the throw, a failed response falls through to response.json() and the retry loop never sees the failure; callers get parsed error pages.',
        fix: 'Throw on !response.ok so the catch block can retry.',
        find: '        throw new Error(`HTTP ${response.status}`);',
        replace: '        console.warn(`HTTP ${response.status}`);',
      },
      {
        id: 'fetch-missing-await',
        category: 'logic',
        tier: 'expert',
        title: 'Returned promise escapes the try/catch',
        explanation:
          'Without await, response.json() rejections happen after the function has already returned the promise, so a truncated body skips the retry logic entirely.',
        fix: 'return await response.json() inside try, so rejections are caught and retried.',
        find: '      return await response.json();',
        replace: '      return response.json();',
      },
    ],
  },
  {
    id: 'js-search-index',
    language: 'javascript',
    title: 'Product tag search index',
    code: [
      'function createSearchIndex(products) {',
      '  const byTag = new Map();',
      '  for (const product of products) {',
      '    for (const tag of product.tags ?? []) {',
      '      if (!byTag.has(tag)) {',
      '        byTag.set(tag, []);',
      '      }',
      '      byTag.get(tag).push(product);',
      '    }',
      '  }',
      '  return function search(tag) {',
      '    return byTag.get(tag) ?? [];',
      '  };',
      '}',
    ],
    mutations: [
      {
        id: 'index-linear-scan',
        category: 'performance',
        tier: 'expert',
        title: 'Search ignores the index it just built',
        explanation:
          'Every lookup rescans all products with filter, turning the O(1) map hit into an O(n) pass: the index is built, paid for, and never used.',
        fix: 'Serve lookups from the map: byTag.get(tag) ?? [].',
        find: '    return byTag.get(tag) ?? [];',
        replace: '    return products.filter((p) => (p.tags ?? []).includes(tag));',
      },
      {
        id: 'index-missing-tags',
        category: 'null-safety',
        tier: 'novice',
        title: 'Products without tags crash the build',
        explanation:
          'Iterating product.tags directly throws "not iterable" the first time a product has no tags field.',
        fix: 'Fall back to an empty list: product.tags ?? [].',
        find: '    for (const tag of product.tags ?? []) {',
        replace: '    for (const tag of product.tags) {',
      },
      {
        id: 'index-overwrite',
        category: 'logic',
        tier: 'intermediate',
        title: 'Each product overwrites the tag bucket',
        explanation:
          'set replaces the whole bucket, so every tag remembers only the last product indexed; earlier products silently vanish from results.',
        fix: 'Append to the existing bucket: byTag.get(tag).push(product).',
        find: '      byTag.get(tag).push(product);',
        replace: '      byTag.set(tag, [product]);',
      },
      {
        id: 'index-keys-scan',
        category: 'performance',
        tier: 'intermediate',
        title: 'O(n) membership test on a Map',
        explanation:
          'Materializing all keys into an array per product-tag pair makes index construction quadratic; Map.has is the O(1) primitive for exactly this.',
        fix: 'Use byTag.has(tag).',
        find: '      if (!byTag.has(tag)) {',
        replace: '      if (!Array.from(byTag.keys()).includes(tag)) {',
      },
      {
        id: 'index-let',
        category: 'style',
        tier: 'novice',
        title: 'let for a never-reassigned binding',
        explanation:
          'The map is mutated but never reassigned; const documents that and lets the reader stop tracking the binding.',
        fix: 'Declare with const.',
        find: '  const byTag = new Map();',
        replace: '  let byTag = new Map();',
      },
    ],
  },
  {
    id: 'py-binary-search',
    language: 'python',
    title: 'Binary search over a sorted list',
    code: [
      'def binary_search(items, target):',
      '    lo = 0',
      '    hi = len(items) - 1',
      '    while lo <= hi:',
      '        mid = (lo + hi) // 2',
      '        value = items[mid]',
      '        if value == target:',
      '            return mid',
      '        if value < target:',
      '            lo = mid + 1',
      '        else:',
      '            hi = mid - 1',
      '    return -1',
    ],
    mutations: [
      {
        id: 'bsearch-stall',
        category: 'logic',
        tier: 'expert',
        title: 'Search can stall forever',
        explanation:
          'lo = mid does not shrink the range when the window is two elements wide (mid rounds down to lo), so the loop spins forever on any miss to the right.',
        fix: 'Advance past the probe: lo = mid + 1.',
        find: '            lo = mid + 1',
        replace: '            lo = mid',
      },
      {
        id: 'bsearch-hi-bound',
        category: 'logic',
        tier: 'novice',
        title: 'Upper bound off the end of the list',
        explanation:
          'hi = len(items) lets mid reach the list length, raising IndexError when the target is greater than every element.',
        fix: 'Start with hi = len(items) - 1 for inclusive bounds.',
        find: '    hi = len(items) - 1',
        replace: '    hi = len(items)',
      },
      {
        id: 'bsearch-loop-cond',
        category: 'logic',
        tier: 'intermediate',
        title: 'Loop quits before checking the last element',
        explanation:
          'With lo < hi the window of exactly one element is never examined, so targets at the convergence point are reported missing.',
        fix: 'Loop while lo <= hi with these inclusive bounds.',
        find: '    while lo <= hi:',
        replace: '    while lo < hi:',
      },
      {
        id: 'bsearch-float-mid',
        category: 'logic',
        tier: 'novice',
        title: 'Float midpoint used as an index',
        explanation:
          'Single-slash division yields a float, and items[2.5] raises TypeError on the first iteration.',
        fix: 'Use floor division: (lo + hi) // 2.',
        find: '        mid = (lo + hi) // 2',
        replace: '        mid = (lo + hi) / 2',
      },
      {
        id: 'bsearch-sentinel',
        category: 'style',
        tier: 'intermediate',
        title: 'Miss sentinel changed under callers',
        explanation:
          'Returning None where the documented contract is -1 breaks every caller that tests result == -1 or uses the result arithmetically.',
        fix: 'Keep the documented sentinel (-1), or change the contract everywhere at once.',
        find: '    return -1',
        replace: '    return None',
      },
    ],
  },
  {
    id: 'js-render-comment',
    language: 'javascript',
    title: 'Comment renderer (user content)',
    code: [
      'function renderComment(comment) {',
      "  const container = document.createElement('article');",
      "  const author = document.createElement('strong');",
      '  author.textContent = comment.author;',
      "  const body = document.createElement('p');",
      '  body.textContent = comment.body;',
      "  const link = document.createElement('a');",
      "  link.textContent = 'profile';",
      "  link.href = encodeURI('/users/' + comment.authorId);",
      "  link.rel = 'noopener noreferrer';",
      "  link.target = '_blank';",
      '  container.append(author, body, link);',
      '  return container;',
      '}',
    ],
    mutations: [
      {
        id: 'comment-body-xss',
        category: 'security',
        tier: 'intermediate',
        title: 'Stored XSS via comment body',
        explanation:
          'innerHTML executes whatever markup the commenter wrote: one <img onerror=...> comment runs script for every reader.',
        fix: 'Assign untrusted text with textContent.',
        find: '  body.textContent = comment.body;',
        replace: '  body.innerHTML = comment.body;',
      },
      {
        id: 'comment-author-xss',
        category: 'security',
        tier: 'novice',
        title: 'Username concatenated into HTML',
        explanation:
          'Building markup by string-concatenating a user-chosen name is the textbook XSS vector; display names are attacker-controlled input.',
        fix: 'Set the name with textContent and style it with CSS.',
        find: '  author.textContent = comment.author;',
        replace: "  author.innerHTML = '<b>' + comment.author + '</b>';",
      },
      {
        id: 'comment-href-inject',
        category: 'security',
        tier: 'expert',
        title: 'Attacker-controlled URL in href',
        explanation:
          "A profile URL taken from the comment record can be javascript:alert(document.cookie); href schemes aren't filtered by the browser.",
        fix: 'Build the URL from the id on a fixed path, or allow only http(s) URLs.',
        find: "  link.href = encodeURI('/users/' + comment.authorId);",
        replace: '  link.href = comment.profileUrl;',
      },
      {
        id: 'comment-rel-blank',
        category: 'security',
        tier: 'intermediate',
        title: 'target=_blank without rel protection',
        explanation:
          'Removing rel lets the opened page reach window.opener and navigate this tab to a phishing clone (reverse tabnabbing) in older browsers, and leaks referrer data.',
        fix: "Keep rel='noopener noreferrer' on every target=_blank link.",
        find: "  link.rel = 'noopener noreferrer';",
        replace: "  link.rel = '';",
      },
      {
        id: 'comment-semantic-div',
        category: 'style',
        tier: 'novice',
        title: 'Semantic article demoted to div',
        explanation:
          'Screen readers and reader modes use the article landmark to navigate comment threads; a bare div erases that structure.',
        fix: 'Use the element that matches the content: article.',
        find: "  const container = document.createElement('article');",
        replace: "  const container = document.createElement('div');",
      },
    ],
  },
  {
    id: 'sql-revenue-report',
    language: 'sql',
    title: 'Monthly revenue report query',
    code: [
      '-- August revenue per customer, top 50',
      'SELECT c.id,',
      '       c.name,',
      '       SUM(o.total_cents) / 100.0 AS revenue',
      'FROM customers AS c',
      'JOIN orders AS o',
      '  ON o.customer_id = c.id',
      "WHERE o.status = 'paid'",
      "  AND o.created_at >= '2026-08-01'",
      "  AND o.created_at < '2026-09-01'",
      'GROUP BY c.id, c.name',
      'ORDER BY revenue DESC',
      'LIMIT 50;',
    ],
    mutations: [
      {
        id: 'sql-date-boundary',
        category: 'logic',
        tier: 'expert',
        title: 'Timestamp boundary drops the last day',
        explanation:
          "With timestamps, <= '2026-08-31' means midnight, so every order after 00:00 on Aug 31 falls out of the report: a silent ~3% revenue error.",
        fix: "Use a half-open range: created_at < '2026-09-01'.",
        find: "  AND o.created_at < '2026-09-01'",
        replace: "  AND o.created_at <= '2026-08-31'",
      },
      {
        id: 'sql-status-filter',
        category: 'logic',
        tier: 'intermediate',
        title: 'Revenue counts unpaid orders',
        explanation:
          "!= 'refunded' admits pending, canceled, and failed orders into revenue; the report overstates income by whatever is stuck in checkout.",
        fix: "Filter to the one state that is revenue: status = 'paid'.",
        find: "WHERE o.status = 'paid'",
        replace: "WHERE o.status != 'refunded'",
      },
      {
        id: 'sql-cast-join',
        category: 'performance',
        tier: 'expert',
        title: 'Casts on the join keys kill index use',
        explanation:
          'Wrapping both join keys in CAST makes the predicate non-sargable: the planner cannot use the customer_id index and falls back to scanning orders.',
        fix: 'Join the columns directly (fix the column types if they differ).',
        find: '  ON o.customer_id = c.id',
        replace: '  ON CAST(o.customer_id AS TEXT) = CAST(c.id AS TEXT)',
      },
      {
        id: 'sql-group-by-name',
        category: 'logic',
        tier: 'novice',
        title: 'Grouping by name merges different customers',
        explanation:
          'Two customers named "Alex Kim" collapse into one row (and selecting c.id ungrouped is invalid SQL in strict modes).',
        fix: 'Group by the key: GROUP BY c.id, c.name.',
        find: 'GROUP BY c.id, c.name',
        replace: 'GROUP BY c.name',
      },
      {
        id: 'sql-int-division',
        category: 'logic',
        tier: 'intermediate',
        title: 'Integer division truncates per row',
        explanation:
          'total_cents / 100 in integer arithmetic drops the cents of every order before summing, understating revenue order by order.',
        fix: 'Divide the sum by a decimal: SUM(total_cents) / 100.0.',
        find: '       SUM(o.total_cents) / 100.0 AS revenue',
        replace: '       SUM(o.total_cents / 100) AS revenue',
      },
    ],
  },
  {
    id: 'js-seat-reservation',
    language: 'javascript',
    title: 'Concurrent seat reservation',
    code: [
      'async function reserveSeat(store, showId, userId) {',
      '  const state = await store.get(showId);',
      '  if (state.available <= 0) {',
      "    return { ok: false, reason: 'sold out' };",
      '  }',
      '  const updated = await store.update(showId, (current) => {',
      '    if (current.available <= 0) {',
      '      return current;',
      '    }',
      '    return {',
      '      ...current,',
      '      available: current.available - 1,',
      '      holders: [...current.holders, userId],',
      '    };',
      '  });',
      '  const reserved = updated.holders.includes(userId);',
      "  return { ok: reserved, reason: reserved ? 'reserved' : 'sold out' };",
      '}',
    ],
    mutations: [
      {
        id: 'seat-stale-check',
        category: 'logic',
        tier: 'expert',
        title: 'Race: re-check reads the stale snapshot',
        explanation:
          'Inside the atomic update the guard consults state (fetched before the update), not current: two concurrent buyers both pass the stale check and the show oversells.',
        fix: 'Guard on the value the updater receives: current.available.',
        find: '    if (current.available <= 0) {',
        replace: '    if (state.available <= 0) {',
      },
      {
        id: 'seat-stale-holders',
        category: 'logic',
        tier: 'expert',
        title: 'Holder list rebuilt from the stale snapshot',
        explanation:
          'Spreading state.holders instead of current.holders erases everyone who reserved between the read and this update: a lost-update race.',
        fix: 'Build the new list from current.holders.',
        find: '      holders: [...current.holders, userId],',
        replace: '      holders: [...state.holders, userId],',
      },
      {
        id: 'seat-missing-await',
        category: 'null-safety',
        tier: 'intermediate',
        title: 'Missing await leaves state a Promise',
        explanation:
          'Without await, state is a pending Promise: state.available is undefined, the sold-out fast path never fires, and the code only works by accident.',
        fix: 'await store.get(showId).',
        find: '  const state = await store.get(showId);',
        replace: '  const state = store.get(showId);',
      },
      {
        id: 'seat-increment',
        category: 'logic',
        tier: 'novice',
        title: 'Seat count increments on reserve',
        explanation:
          'Reserving a seat should decrease availability; += 1 mints seats out of thin air and the show never sells out.',
        fix: 'Decrement: current.available - 1.',
        find: '      available: current.available - 1,',
        replace: '      available: current.available + 1,',
      },
    ],
  },
];
